import { createContext, type ReactNode, useContext } from "react";
import { useT } from "~/core/i18n";

/**
 * What the person reading this screen may change.
 *
 * The server decides this — every write route asks again, and a screen is one
 * fetch away from being bypassed — so nothing here is a security boundary. It
 * is the difference between a button that refuses and a button that says, on
 * sight, that it is not yours to press.
 *
 * Two answers, because there are two roles. `editor` is about the brand the
 * workspace is open on; `admin` is about the account it belongs to — its money,
 * its plan, its brands, who is in it. A person can easily be one and not the
 * other: the usual member of an agency account edits two brands and pays for
 * nothing.
 *
 * And one fact about the account that overrides both: a plan that has lapsed
 * reads everything and changes nothing. The ways out of that — paying, pausing,
 * cancelling — are the account's own controls and do not read this.
 */
export interface Access {
	/** Editor on the brand this screen is about. */
	editor: boolean;
	/** Owner or admin of the account. */
	admin: boolean;
	/** The account's plan is live. */
	operable: boolean;
	/** Paid execution is separate from viewing and editing brand configuration. */
	subscribed?: boolean;
	/**
	 * Whether the plan includes the Agent, and whether it includes the
	 * readings that are not part of the base loop. Neither is a role and
	 * neither is a refusal: the rows are not drawn at all, and the server
	 * refuses their endpoints.
	 */
	agent?: boolean;
	advanced?: boolean;
}

/**
 * The answers a control reads, with the sentence for each refusal.
 *
 * `editor` and `admin` here are already `false` for a lapsed account, so the
 * thirty `disabled={!editor}` fieldsets in the panel close without each one
 * knowing why; `why` is the sentence for the reason that applies, so the title
 * at the point of the click says "lapsed" and not "viewer" when it is the plan.
 */
export interface Allowed extends Access {
	why: { editor: string; admin: string };
}

/**
 * Open by default.
 *
 * The screens outside a brand — the wizard, the claim flow — have no brand to
 * hold a role on, and a default of "no" would grey out the one button somebody
 * arriving there came to press.
 */
const AccessContext = createContext<Access>({ editor: true, admin: true, operable: true });

export function AccessProvider({ value, children }: { value: Access; children: ReactNode }) {
	return <AccessContext.Provider value={value}>{children}</AccessContext.Provider>;
}

export function useAccess(): Allowed {
	const t = useT();
	const raw = useContext(AccessContext);
	const lapsed = t("banner.expired");
	return {
		editor: raw.editor && raw.operable,
		admin: raw.admin && raw.operable,
		operable: raw.operable,
		subscribed: raw.subscribed !== false,
		agent: raw.agent !== false,
		advanced: raw.advanced !== false,
		why: {
			editor: raw.operable ? t("access.viewer") : lapsed,
			admin: raw.operable ? t("access.admin") : lapsed,
		},
	};
}

/** Pausing or cancelling still needs the brand role after the plan lapses. */
export function useExitAccess(): Pick<Access, "editor" | "admin"> {
	const { editor, admin } = useContext(AccessContext);
	return { editor, admin };
}

/**
 * The sentence a disabled control carries, or nothing when it is not disabled.
 *
 * A native `title`, not a tooltip component: these hang off controls that are
 * disabled, and a Radix trigger wrapped around a disabled button never receives
 * the pointer events it needs to open. The banner at the top of the screen is
 * where the full explanation lives; this is the reminder at the point of the
 * click.
 */
export function whyDisabled(allowed: boolean, reason: string): string | undefined {
	return allowed ? undefined : reason;
}
